import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Check, Lock, ShieldCheck, CheckCircle2, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { usd2, platformFeeRate } from '../lib/format';
import { API } from '../lib/api';

export default function CheckoutModal({ product, open, onOpenChange, plan = 'free', handle }) {
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setBusy(false); }, [open, product]);
  if (!product) return null;

  const rate = platformFeeRate(plan);
  const fee = product.price * rate;
  const net = product.price - fee;
  const isReal = !!product.lookup_key;

  const startCheckout = async () => {
    if (!isReal) {
      // Non-catalog item (e.g. flash drop) — simulated confirmation
      setBusy(true);
      setTimeout(() => { setBusy(false); onOpenChange(false); toast.success('Reserved! Check your email to complete.'); }, 1200);
      return;
    }
    setBusy(true);
    try {
      const { data } = await API.post('/payments/checkout', {
        lookup_key: product.lookup_key,
        origin_url: window.location.origin,
        handle,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong max-w-md gap-0 overflow-hidden rounded-3xl border-white/10 p-0 text-white">
        <div className="relative h-28 w-full overflow-hidden">
          <img src={product.image} alt={product.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/40 to-transparent" />
        </div>
        <div className="relative -mt-6 px-6 pb-6">
          <span className="text-[11px] font-medium uppercase tracking-wider text-primary">{product.type}</span>
          <h2 className="font-display text-xl font-bold leading-tight">{product.title}</h2>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-display text-2xl font-bold">{usd2(product.price)}</span>
            {product.compareAt && <span className="text-sm text-muted-foreground line-through">{usd2(product.compareAt)}</span>}
          </div>

          {product.highlights && (
            <ul className="mt-3 space-y-1.5">
              {product.highlights.slice(0, 4).map((h) => (
                <li key={h} className="flex items-center gap-2 text-sm text-white/80"><Check className="h-4 w-4 shrink-0 text-primary" /> {h}</li>
              ))}
            </ul>
          )}

          <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm">
            <div className="flex items-center justify-between text-white/80"><span>Order total (USD)</span><span className="font-medium text-white">{usd2(product.price)}</span></div>
            <div className="mt-1.5 flex items-center justify-between text-white/60">
              <span>Platform fee ({plan === 'pro' ? '0%' : '9%'} · {plan === 'pro' ? 'Pro' : 'Free'})</span>
              <span>{rate === 0 ? '$0.00' : `-${usd2(fee)}`}</span>
            </div>
            <div className="my-2 h-px bg-white/10" />
            <div className="flex items-center justify-between"><span className="text-white/80">Creator receives</span><span className="font-display font-bold text-primary">{usd2(net)}</span></div>
          </div>

          <Button onClick={startCheckout} disabled={busy} className="mt-5 h-12 w-full rounded-xl bg-primary text-base font-bold text-primary-foreground hover:bg-primary/90 glow-lime">
            {busy ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting…</>) : (<>{isReal ? <>Pay {usd2(product.price)} — Secure Checkout <ExternalLink className="ml-1.5 h-4 w-4" /></> : <>Reserve my spot</>}</>)}
          </Button>
          <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" /> Powered by Stripe Connect · {isReal ? 'You\'ll complete payment on Stripe' : 'Demo reservation'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
